import type { ComposerScene } from '@vellnes/composer-react';
import type { InstanceBounds } from '@vellnes/composer-core';

import { roundNumber } from '../../../../utils/roundNumber';
import { eulerToQuaternion } from '../../../../utils/threejs/eulerToQuaternion';
import { vec3FromTuple } from '../../../../utils/threejs/vec3FromTuple';

import {
  CompositeRenderCamera,
  CompositeRenderModel
} from '../../../renderGenerations/renderGenerationsTypes';

const UP_VECTOR = { x: 0, y: 1, z: 0 };

export function buildCompositeModels(
  scene: ComposerScene,
  bounds: Record<string, InstanceBounds>
): CompositeRenderModel[] {
  return scene.instances.map((instance) => {
    const instanceBounds = bounds[instance.instanceId];
    const min = instanceBounds
      ? vec3FromTuple(instanceBounds.min as [number, number, number])
      : { x: 0, y: 0, z: 0 };
    const max = instanceBounds
      ? vec3FromTuple(instanceBounds.max as [number, number, number])
      : { x: 0, y: 0, z: 0 };

    return {
      id: instance.sourceProductId,
      transform: {
        position: vec3FromTuple(instance.transform.position),
        rotation: eulerToQuaternion(instance.transform.rotationEuler),
        scale: vec3FromTuple(instance.transform.scale)
      },
      bbox: {
        source: 'client_estimated',
        world: {
          min,
          max,
          size: {
            x: roundNumber(max.x - min.x),
            y: roundNumber(max.y - min.y),
            z: roundNumber(max.z - min.z)
          },
          center: {
            x: roundNumber((min.x + max.x) / 2),
            y: roundNumber((min.y + max.y) / 2),
            z: roundNumber((min.z + max.z) / 2)
          }
        }
      }
    };
  });
}

export function buildCompositeBackgroundHex(
  scene: ComposerScene
): string | undefined {
  const { background } = scene;
  return background.mode === 'color'
    ? background.color
    : background.gradient?.top;
}

export function buildCompositeCameras(
  scene: ComposerScene,
  aspect: number
): CompositeRenderCamera[] {
  return scene.cameras.map((camera) => ({
    id: camera.cameraId,
    name: camera.name,
    type: 'perspective',
    transform: {
      position: vec3FromTuple(camera.position),
      look_at: {
        target: vec3FromTuple(camera.target),
        up: UP_VECTOR
      }
    },
    projection: {
      fov_axis: 'vertical',
      fov_vertical_deg: roundNumber(camera.fov, 4),
      aspect: roundNumber(aspect, 5),
      near: camera.near,
      far: camera.far,
      zoom: camera.zoom,
      film_gauge_mm: camera.filmGauge,
      film_offset_mm: camera.filmOffset
    },
    dof: {
      enabled: camera.dofEnabled,
      focus_distance: camera.dofFocusDistance,
      f_stop: camera.dofFStop,
      aperture_blades: camera.dofApertureBlades,
      aperture_rotation_deg: camera.dofApertureRotationDeg,
      aperture_ratio: camera.dofApertureRatio
    }
  }));
}
